// Unpublished Work © 2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Typography} from '@deere/isg.component-library';
import ReadOnlyWrapperComponent from 'Ui/components/common/form/read-only/read-only-wrapper-component';
import {Paper, Popper} from '@mui/material';
import {formatTime} from 'Utils/time-utils';
import {THIRTY_DAYS} from 'Ui/constants/time-constants';
import moment from 'moment';
import ValidationInput from 'Src/ui-core/components/common/form/validation-input';

const MAX_DATE_KEYS = 3;
const POPOVER_TIME = 200;

function addPinLocationFromWorkboardToMap(pinLocationHistoryMap, isNewDateSelect, {
    dateSelect,
    pinLocation
}) {
    if (!pinLocation) {
        return;
    }

    if (isNewDateSelect) {
        pinLocationHistoryMap.set(dateSelect, []);
    }

    pinLocationHistoryMap.get(dateSelect).push(pinLocation);
}

function PinLocation(props) {
    const {
        adornment,
        disabled,
        onChange,
        pinLocation,
        placeholder = '',
        readOnly,
        setValid,
        translations,
        workboards
    } = props;

    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleFocus = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleBlur = () => {
        // Cierra el popover al perder el foco
        setTimeout(() => setAnchorEl(null), POPOVER_TIME);
    };

    const width = anchorEl ? anchorEl.parentElement?.offsetWidth : undefined;

    const pinLocationElements = React.useMemo(() => {
        const pinLocationHistoryMap = new Map();
        const thirtyDaysAgo = moment().subtract(THIRTY_DAYS, 'days');

        for (let i = workboards.length - 1; i >= 0; i--) {
            const workboard = workboards[i];
            const isNewDateSelect = !pinLocationHistoryMap.has(workboard.dateSelect);

            if (pinLocationHistoryMap.size >= MAX_DATE_KEYS && isNewDateSelect ||
                thirtyDaysAgo.isAfter(workboard.dateSelect, 'day')) {
                break;
            }

            addPinLocationFromWorkboardToMap(pinLocationHistoryMap, isNewDateSelect, workboard);
        }
        const finalArray = [];

        for (const [dateSelect, pinLocationsForDateSelect] of pinLocationHistoryMap) {
            finalArray.push(`${formatTime(dateSelect, 'MMM DD, YYYY')} - ${pinLocationsForDateSelect.join(', ')}`);
        }
        return finalArray;
    }, [workboards]);

    return (
        <div>
            <ReadOnlyWrapperComponent
                label={translations.PRO_GOLF_PIN_LOCATION}
                readOnly={readOnly}
                readOnlyProps={{
                    readOnlyLabelClassName: 'title-1',
                    readOnlyValueClassName: 'workboard-details-readonly-value'
                }}
                style={{
                    width: '100%'
                }}
                value={pinLocation || ''}
                wrappedComponent={ValidationInput}
                wrappedProps={{
                    disabled,
                    errors: {
                        tooLong: translations.PRO_GOLF_PIN_LOCATION_TOO_LONG_ERROR
                    },
                    InputProps: {
                        endAdornment: adornment
                    },
                    maxLength: 2,
                    name: 'pinLocation',
                    onChange,
                    onFocus: handleFocus,
                    onBlur: handleBlur,
                    placeholder,
                    setValid,
                    tabIndex: 0,
                    type: 'text'
                }}
            />
            <Popper
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && pinLocationElements.length > 0}
                placement='bottom-start'
                sx={{
                    zIndex: 1500,
                    pointerEvents: 'none'
                }}
            >
                <Paper
                    sx={{
                        width,
                        marginLeft: '-10px',
                        paddingInline: '16px',
                        paddingTop: '10px',
                        position: 'fixed'
                    }}
                >
                    {pinLocationElements.map((val, i) => (
                        <Typography
                            color='text.primary'
                            display={'block'}
                            key={i}
                            sx={{
                                marginBottom: '10px'
                            }}
                            variant='bodyMedium'
                        >
                            {val}
                        </Typography>
                    ))}
                </Paper>
            </Popper>
        </div>
    );
}

PinLocation.propTypes = {
    adornment: PropTypes.object,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    pinLocation: PropTypes.string,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    setValid: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object,
    workboards: PropTypes.arrayOf(PropTypes.object)
};

export default PinLocation;
